import { logger } from "@/lib/logger"
import type { JsonObject } from "@/types/json"

import type {
  EditableTestCase,
  PendingChanges,
  TestSetMetadata,
} from "../../model"

const log = logger("TestSetEditorEventBus")

// Event types
export const EVENTS = {
  TEST_CASE_SELECT: "TEST_CASE_SELECT",
  TEST_CASE_UPDATE: "TEST_CASE_UPDATE",
  TEST_CASE_SIMULATION_METADATA_UPDATE: "TEST_CASE_SIMULATION_METADATA_UPDATE",
  TEST_CASE_DELETE: "TEST_CASE_DELETE",
  TEST_CASE_ADD: "TEST_CASE_ADD",
  PERSISTED_TEST_CASE_ADD: "PERSISTED_TEST_CASE_ADD",
  TEST_CASE_COPY: "TEST_CASE_COPY",
  METADATA_UPDATE: "METADATA_UPDATE",
  API_SETUP_APPLY: "API_SETUP_APPLY",
  HYDRATE_FROM_SERVER: "HYDRATE_FROM_SERVER",
  COPILOT_UPDATE: "COPILOT_UPDATE",
  COPILOT_ADD: "COPILOT_ADD",
  COPILOT_DELETE: "COPILOT_DELETE",
  PENDING_CHANGES_UPDATE: "PENDING_CHANGES_UPDATE",
  SAVE_REQUEST: "SAVE_REQUEST",
  SAVE_COMPLETE: "SAVE_COMPLETE",
  CANCEL_REQUEST: "CANCEL_REQUEST",
  ACCEPT_PENDING_EDIT: "ACCEPT_PENDING_EDIT",
  DENY_PENDING_EDIT: "DENY_PENDING_EDIT",
  ACCEPT_PENDING_ADDITION: "ACCEPT_PENDING_ADDITION",
  DENY_PENDING_ADDITION: "DENY_PENDING_ADDITION",
  ACCEPT_PENDING_DELETION: "ACCEPT_PENDING_DELETION",
  DENY_PENDING_DELETION: "DENY_PENDING_DELETION",
  UNDO: "UNDO",
  REVERT_TO_HISTORY: "REVERT_TO_HISTORY",
  EXPAND_ALL: "EXPAND_ALL",
  COLLAPSE_ALL: "COLLAPSE_ALL",
  TEST_CASE_TOGGLE_SELECT: "TEST_CASE_TOGGLE_SELECT",
  TEST_CASE_SELECT_ALL: "TEST_CASE_SELECT_ALL",
  TEST_CASE_CLEAR_SELECTION: "TEST_CASE_CLEAR_SELECTION",
  BULK_ADD: "BULK_ADD",
  BULK_DELETE: "BULK_DELETE",
  EXPAND_PENDING_CHANGES: "EXPAND_PENDING_CHANGES",
  OPEN_VERSION_HISTORY: "OPEN_VERSION_HISTORY",
} as const

export type EventType = (typeof EVENTS)[keyof typeof EVENTS]

// Event payloads
export interface EventPayloads {
  [EVENTS.API_SETUP_APPLY]: {
    config: NonNullable<TestSetMetadata["simulationWrapperTopLevelConfig"]>
  }
  [EVENTS.TEST_CASE_SELECT]: { testCaseId: string }
  [EVENTS.TEST_CASE_UPDATE]: {
    testCaseId: string
    updates: Partial<EditableTestCase>
  }
  [EVENTS.TEST_CASE_SIMULATION_METADATA_UPDATE]: {
    testCaseId: string
    updates: JsonObject
  }
  [EVENTS.TEST_CASE_DELETE]: { testCaseId: string }
  [EVENTS.TEST_CASE_ADD]: { testCase: EditableTestCase }
  [EVENTS.PERSISTED_TEST_CASE_ADD]: { testCase: EditableTestCase }
  [EVENTS.TEST_CASE_COPY]: { testCaseId: string }
  [EVENTS.METADATA_UPDATE]: {
    displayName?: string
    description?: string
    simulationWrapperType?: TestSetMetadata["simulationWrapperType"]
    simulationWrapperTopLevelConfig?: TestSetMetadata["simulationWrapperTopLevelConfig"]
  }
  [EVENTS.HYDRATE_FROM_SERVER]: {
    metadata: {
      displayName: string
      description?: string
      createdAt?: string
      lastUpdatedAt?: string
      createdByUserId?: string | null
      simulationWrapperType?: TestSetMetadata["simulationWrapperType"]
      simulationWrapperTopLevelConfig?: TestSetMetadata["simulationWrapperTopLevelConfig"]
    }
    testCases: EditableTestCase[]
    selectedAgentIds: string[]
    activeAgentId: string
  }
  [EVENTS.COPILOT_UPDATE]: {
    updates: Array<{ id: string; updates: Partial<EditableTestCase> }>
  }
  [EVENTS.COPILOT_ADD]: { testCases: EditableTestCase[] }
  [EVENTS.COPILOT_DELETE]: { testCaseIds: string[] }
  [EVENTS.PENDING_CHANGES_UPDATE]: { changes: PendingChanges | null }
  [EVENTS.SAVE_REQUEST]: Record<string, never>
  [EVENTS.SAVE_COMPLETE]: Record<string, never>
  [EVENTS.CANCEL_REQUEST]: Record<string, never>
  [EVENTS.ACCEPT_PENDING_EDIT]: { testCaseId: string }
  [EVENTS.DENY_PENDING_EDIT]: { testCaseId: string }
  [EVENTS.ACCEPT_PENDING_ADDITION]: { index: number }
  [EVENTS.DENY_PENDING_ADDITION]: { index: number }
  [EVENTS.ACCEPT_PENDING_DELETION]: { testCaseId: string }
  [EVENTS.DENY_PENDING_DELETION]: { testCaseId: string }
  [EVENTS.UNDO]: Record<string, never>
  [EVENTS.REVERT_TO_HISTORY]: { historyIndex: number }
  [EVENTS.EXPAND_ALL]: Record<string, never>
  [EVENTS.COLLAPSE_ALL]: Record<string, never>
  [EVENTS.TEST_CASE_TOGGLE_SELECT]: {
    testCaseId: string
    index: number
    shiftKey: boolean
  }
  [EVENTS.TEST_CASE_SELECT_ALL]: Record<string, never>
  [EVENTS.TEST_CASE_CLEAR_SELECTION]: Record<string, never>
  [EVENTS.BULK_ADD]: { testCases: EditableTestCase[] }
  [EVENTS.BULK_DELETE]: { testCaseIds: string[] }
  [EVENTS.EXPAND_PENDING_CHANGES]: Record<string, never>
  [EVENTS.OPEN_VERSION_HISTORY]: Record<string, never>
}

// Event listener type
export type EventListener<T extends EventType> = (
  payload: EventPayloads[T],
) => void

// Event bus class
class EventBus {
  private listeners: Map<EventType, Set<EventListener<EventType>>> = new Map()

  // Subscribe to an event
  subscribe<T extends EventType>(
    eventType: T,
    listener: EventListener<T>,
  ): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set())
    }

    this.listeners.get(eventType)!.add(listener as EventListener<EventType>)

    // Return unsubscribe function
    return () => {
      const eventListeners = this.listeners.get(eventType) as
        | Set<EventListener<T>>
        | undefined
      if (eventListeners) {
        eventListeners.delete(listener)
        if (eventListeners.size === 0) {
          this.listeners.delete(eventType)
        }
      }
    }
  }

  // Dispatch an event
  dispatch<T extends EventType>(eventType: T, payload: EventPayloads[T]): void {
    const eventListeners = this.listeners.get(eventType) as
      | Set<EventListener<T>>
      | undefined
    if (eventListeners) {
      eventListeners.forEach((listener) => {
        try {
          listener(payload)
        } catch (error) {
          log.error(`Error in event listener for ${eventType}`, error)
        }
      })
    }
  }

  // Remove all listeners for a specific event type
  removeAllListeners(eventType: EventType): void {
    this.listeners.delete(eventType)
  }

  // Remove all listeners
  clear(): void {
    this.listeners.clear()
  }
}

// Create and export a singleton instance
export const testSetEditorEventBus = new EventBus()
